import { useRef } from 'react';
import { NavigationBar } from '@theswing/rhythm';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { PAGE_MAX_WIDTH } from '@/constants';
import { normalizeHexColor } from '@/utils';

interface NavigationBarContainerProps {
  background?: string;
}

const NavigationBarContainer = styled.div<NavigationBarContainerProps>`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: ${props => props.background ?? '#fff'};
  ${props => !!PAGE_MAX_WIDTH ? 
    `max-width: ${PAGE_MAX_WIDTH}px;
    left: calc(50% - min(${PAGE_MAX_WIDTH}px, 100%) / 2);` : ''};
`;

interface ButtonContainerProps {
  backgroundColor?: string;
}

const ButtonContainer = styled.div<ButtonContainerProps>`
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;  
  background: ${props => {
    const color = normalizeHexColor(props.backgroundColor ?? '#ffffff');
    return `linear-gradient(180deg, ${color}00 0%, ${color} 57%)`;
  }};
`;

interface ContentProps { 
  isScrollable?: boolean;
  background?: string;
  hasButtonWith?: [ 'gradient' | 'solid', number, string? ];
  children?: React.ReactNode;
}

const Content = styled.div<ContentProps>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  overflow-y: ${props => props.isScrollable ? 'scroll' : 'hidden'};
  background: ${props => props.background ?? '#fff'};
  ${props => !!PAGE_MAX_WIDTH ? `max-width: ${PAGE_MAX_WIDTH}px` : ''};
  ${props => {
    if (props.hasButtonWith == null || !Array.isArray(props.hasButtonWith)) return 'padding-bottom: 0;';
    if (props.hasButtonWith[1] < 1) return `padding-bottom: 0;`;

    let padding = 60 * props.hasButtonWith[1];
    padding += props.hasButtonWith[0] === 'gradient' ? (12 * (props.hasButtonWith[1] - 1)) + (16 * 2) : 0;

    return `padding-bottom: ${padding}px;`;
  }};

  & > ${ButtonContainer} {
  ${props => {
    if (props.hasButtonWith == null || !Array.isArray(props.hasButtonWith)) return '';
    if (props.hasButtonWith[1] < 1) return ``;

    const color = normalizeHexColor(props.hasButtonWith[2] ?? '#ffffff');
    const gradientStop = Math.floor(100 / (props.hasButtonWith[1] * 2)) + 7;
    
    return `background: linear-gradient(180deg, ${color}00 0%, ${color} ${gradientStop}%);`;
  }};
  }
  
  & > * {
    width: 100%;
    flex-shrink: 0;
  }
`;

interface LayerProps {
  background?: string;
}

const Layer = styled.div<LayerProps>`
  width: 100%;
  background: ${props => props.background ?? '#fff'};
`;

interface MotionContainerProps {
  margin: string;
}

const MotionContainer = styled(motion.div)<MotionContainerProps>`
  width: 100%;
  height: fit-content;
`;

interface ContainerProps {
  background?: string;
  navUnderControl?: 'webview' | 'native';
  hasSafeBottom?: boolean;
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100vh;  
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  background: ${props => props.background ?? '#fff'};

  & > ${NavigationBarContainer}:first-child {
    ${props => props.navUnderControl === 'native' ? `` : 
    `
      padding-top: constant(safe-area-inset-top);
      padding-top: env(safe-area-inset-top);
    `};
  }

  & > ${Content}:first-child {
    ${props => props.navUnderControl === 'native' ? `` : 
    `
      height: calc(100% - constant(safe-area-inset-top) - ${props.hasSafeBottom ? '0px' : 'constant(safe-area-inset-bottom)'});  
      height: calc(100% - env(safe-area-inset-top) - ${props.hasSafeBottom ? '0px' : 'env(safe-area-inset-bottom)'});
    `};
  }

  & > ${Content}:nth-child(2) {
    height: calc(100% - 64px);
    margin-top: 64px;
    ${props => props.navUnderControl === 'native' ? 
    `
      height: calc(100% - 64px - ${props.hasSafeBottom ? '0px' : 'constant(safe-area-inset-bottom)'});
      height: calc(100% - 64px - ${props.hasSafeBottom ? '0px' : 'env(safe-area-inset-bottom)'});
    ` :
    `
      height: calc(100% - 64px - constant(safe-area-inset-top) - ${props.hasSafeBottom ? '0px' : 'constant(safe-area-inset-bottom)'});
      height: calc(100% - 64px - env(safe-area-inset-top) - ${props.hasSafeBottom ? '0px' : 'env(safe-area-inset-bottom)'});
      margin-top: calc(64px + constant(safe-area-inset-top));
      margin-top: calc(64px + env(safe-area-inset-top));
    `};
  }

  @supports (-webkit-touch-callout: none) { 
    max-height: -webkit-fill-available;
  }
`;

interface PageContainerProps {
  background?: string,  // 페이지 컨테이너의 백그라운드
  children: React.ReactNode,
  hasSafeBottom?: boolean;
  navUnderControl?: 'webview' | 'native'  // 내비게이션바(safe-area-inset-top 포함)의 제어권이 webview에 있는지 native에 있는지에 따라 하위 엘리먼트들의 height, margin 등이 달라짐 
  className?: string;
}

export function PageContainer({ background, children, navUnderControl = 'webview', hasSafeBottom, className }: PageContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Container navUnderControl={navUnderControl} 
               hasSafeBottom={hasSafeBottom}
               background={background}
               ref={containerRef}
               className={className}>
      {children}
    </Container>
  );
}

export interface NavigationBarProps {
  // 기본적으로 디자인 시스템의 props와 동일 + @
  variant: 'part' | 'main',
  activeIndex?: number,
  titleList?: {
    title: string,
    handler?: (params?: any) => void,
  }[],
  titleAlign?: 'left' | 'center',
  title?: string,
  navOption?: {
    type: 'back' | 'close',
    handler?: (params?: any) => void,
    isOpaque?: boolean
  },
  actionList?: {
    type: 'text' | 'image',
    icon: any,
    handler?: (params?: any) => void,
    isOpaque?: boolean
  }[],
  mode?: 'light' | 'dark',
  background?: string // 내비게이션 *컨테이너*의 백그라운드 컬러
}

PageContainer.NavigationBar = (props: NavigationBarProps) => {
  return (
    <NavigationBarContainer background={props.background}>
      <NavigationBar {...props} />
    </NavigationBarContainer>
  );
};

interface MotionContainerProps {
  keyName: string;
  children?: React.ReactNode;
}

PageContainer.MotionContainer = ({ children, keyName }: MotionContainerProps) => {
  return (
    <MotionContainer key={keyName}
                     initial={{ x: '100%' }}
                     animate={{ x: 0 }}
                     transition={{
                       ease: [ 0.32, 1, 0.23, 1 ],
                       duration: .6,
                     }}>
      {children}
    </MotionContainer>
  );
};
PageContainer.Content = Content as React.ComponentType<ContentProps & React.ComponentPropsWithRef<'div'>>;
PageContainer.GhostNavigationBar = NavigationBarContainer;
PageContainer.Layer = Layer as React.ComponentType<LayerProps & React.ComponentPropsWithRef<'div'>>;
PageContainer.ButtonContainer = ButtonContainer;
